import type { ComponentType } from 'react';
import { Head } from 'vite-react-ssg';
import SEO, { jsonLd, type Crumb } from '@/components/SEO';
import PageHeader from '@/components/PageHeader';
import CTABand from '@/components/CTABand';
import type { ToolEntry } from '@/tools/registry';
import { SITE_NAME, SITE_ORIGIN, absoluteUrl } from '@/lib/site';

// Wrapper for every /tools/<slug> route: SEO + breadcrumbs + WebApplication
// JSON-LD + the tool itself + the engineer CTA band. Tools render on cream;
// keep their UI to 1px rules and the palette. The tool component supplies its
// own intro, "how to read the result" and FAQ sections.
const ToolPage = ({ tool, Tool }: { tool: ToolEntry; Tool: ComponentType }) => {
  const path = `/tools/${tool.slug}`;
  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'Tools', href: '/tools' },
    { name: tool.title, href: path },
  ];
  const appSchema = {
    '@context': 'https://schema.org',
    '@type': ['WebApplication', 'SoftwareApplication'],
    name: tool.title,
    description: tool.description,
    url: absoluteUrl(path),
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    browserRequirements: 'Requires JavaScript',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    ...(tool.updated ? { dateModified: tool.updated } : {}),
    publisher: { '@type': 'Organization', '@id': `${SITE_ORIGIN}/#organization`, name: SITE_NAME },
  };
  return (
    <>
      <SEO title={tool.title} description={tool.description} path={path} breadcrumbs={crumbs} />
      <Head>
        <script type="application/ld+json">{jsonLd(appSchema)}</script>
      </Head>
      <PageHeader crumbs={crumbs} label="Tool" title={tool.title} lead={tool.description} />
      <section className="container py-12 md:py-16">
        <Tool />
      </section>
      <CTABand variant="engineer" />
    </>
  );
};

export default ToolPage;
